import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { mesDocumentsAPI } from '../services/api';
import {
  Timer, ReceiptText, CalendarX, FolderOpen, ArrowRight,
  CheckCircle2, Clock, AlertCircle, ChevronRight,
  FileText, Calendar, TrendingUp, Bell,
} from 'lucide-react';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
                   'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const TS_CFG = {
  BROUILLON: { label: 'En cours de saisie',      color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',  icon: Clock },
  SOUMIS:    { label: 'Soumise · en validation', color: '#0891B2', bg: 'rgba(8,145,178,0.12)',   icon: Clock },
  VALIDE:    { label: 'Validée',                 color: '#059669', bg: 'rgba(5,150,105,0.12)',   icon: CheckCircle2 },
  REJETE:    { label: 'Rejetée · à corriger',    color: '#DC2626', bg: 'rgba(220,38,38,0.12)',  icon: AlertCircle },
};

function StatCard({ icon: Icon, color, label, value, sub, to, badge, navigate }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={() => to && navigate(to)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'var(--bg-card)',
        border: `1.5px solid ${hov && to ? color : 'var(--border)'}`,
        borderRadius: 14, padding: '20px 22px',
        cursor: to ? 'pointer' : 'default',
        transform: hov && to ? 'translateY(-3px)' : 'none',
        boxShadow: hov && to ? `0 10px 28px ${color}20` : '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'all 0.18s', position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: '14px 14px 0 0' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, marginTop: 6 }}>
        <div style={{ width: 44, height: 44, borderRadius: 11, background: `${color}16`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={22} color={color} />
        </div>
        {to && <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />}
      </div>
      <div style={{ fontSize: '2.25rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.8rem', fontWeight: 700, color, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3 }}>{sub}</div>
      {badge && (
        <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 4,
                       padding: '3px 9px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700,
                       background: `${color}14`, color }}>
          <Bell size={9} />{badge}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, color, label, desc, to, navigate }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => navigate(to)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? `${color}07` : 'var(--bg-card)',
        border: `1.5px solid ${hov ? color : 'var(--border)'}`,
        borderRadius: 13, padding: '16px 18px', textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 14,
        transform: hov ? 'translateY(-2px)' : 'none',
        boxShadow: hov ? `0 6px 20px ${color}18` : '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'all 0.18s', width: '100%',
      }}
    >
      <div style={{ width: 44, height: 44, borderRadius: 11, background: `${color}14`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={22} color={color} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)' }}>{label}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
      </div>
      <ArrowRight size={16} style={{ color: hov ? color : 'var(--text-muted)', flexShrink: 0, transition: 'color 0.15s' }} />
    </button>
  );
}

export default function CollaborateurDashboard() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const now   = new Date();
  const year  = now.getFullYear();
  const month = now.getMonth() + 1;

  const [timesheet,  setTimesheet]  = useState(null);
  const [notesFrais, setNotesFrais] = useState([]);
  const [absences,   setAbsences]   = useState([]);
  const [docs,       setDocs]       = useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get(`/timesheets/${year}/${month}`),
      api.get('/notes-frais'),
      api.get('/absences'),
      mesDocumentsAPI.list(),
    ]).then(([ts, nf, ab, dc]) => {
      if (ts.status === 'fulfilled') setTimesheet(ts.value.data);
      if (nf.status === 'fulfilled') setNotesFrais(Array.isArray(nf.value.data) ? nf.value.data : []);
      if (ab.status === 'fulfilled') {
        const d = ab.value.data;
        setAbsences(Array.isArray(d) ? d : (d?.items || []));
      }
      if (dc.status === 'fulfilled') setDocs(Array.isArray(dc.value.data) ? dc.value.data : []);
      setLoading(false);
    });
  }, []);

  const prenom    = user?.prenom || 'Collaborateur';
  const tsCfg     = timesheet ? (TS_CFG[timesheet.statut] || TS_CFG.BROUILLON) : null;
  const TsIcon    = tsCfg?.icon || Timer;
  const nfPending = notesFrais.filter(n => ['EN_ATTENTE','SOUMIS'].includes(n.statut)).length;
  const abPending = absences.filter(a  => ['EN_ATTENTE','SOUMIS'].includes(a.statut)).length;
  const docsRejetes = docs.filter(d => d.statut === 'REJETE').length;

  const weekNum = Math.ceil(now.getDate() / 7);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', background: 'var(--bg-base)' }}>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
        padding: '44px 48px 44px',
        position: 'relative', overflow: 'hidden', flexShrink: 0,
      }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280,
                      borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -50, right: 160, width: 180, height: 180,
                      borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 30, right: 260, width: 80, height: 80,
                      borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500, marginBottom: 10,
                        textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {MONTHS_FR[month - 1]} {year}
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: '2.25rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.03em' }}>
            Bonjour, {prenom}
          </h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>
            Votre espace personnel · activité, frais et documents.
          </p>

          {/* Feuille du mois inline dans le hero */}
          <div style={{
            marginTop: 28, display: 'inline-flex', alignItems: 'center', gap: 14,
            background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: 14,
            padding: '14px 20px',
          }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(255,255,255,0.15)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {loading ? <Timer size={20} color="rgba(255,255,255,0.8)" /> : <TsIcon size={20} color="#fff" />}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#fff' }}>
                Feuille de temps · {MONTHS_FR[month - 1]}
              </div>
              {loading ? (
                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>Chargement…</div>
              ) : timesheet ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4,
                                 padding: '2px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
                                 background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
                    <TsIcon size={10} />{tsCfg?.label}
                  </span>
                  {timesheet.total_jours != null && (
                    <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)' }}>
                      {timesheet.total_jours}j saisi{timesheet.total_jours !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>
                  Non commencée · pensez à la remplir
                </div>
              )}
            </div>
            <button
              onClick={() => navigate(`/timesheets/${year}/${month}`)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.8125rem', transition: 'background 0.15s',
                marginLeft: 8,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            >
              {timesheet ? 'Voir' : 'Commencer'} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Corps ────────────────────────────────────────────────── */}
      <div style={{ padding: '36px 48px 48px', flex: 1 }}>

        {/* Alertes */}
        {!loading && docsRejetes > 0 && (
          <div onClick={() => navigate('/mes-documents')}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 18px', borderRadius: 11, marginBottom: 24, cursor: 'pointer',
              background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.25)',
            }}>
            <AlertCircle size={18} style={{ color: '#DC2626', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#DC2626' }}>
                {docsRejetes} document{docsRejetes > 1 ? 's' : ''} rejeté{docsRejetes > 1 ? 's' : ''}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                Consultez vos documents pour voir le motif et les corriger.
              </div>
            </div>
            <ChevronRight size={16} style={{ color: '#DC2626' }} />
          </div>
        )}

        {/* Stats */}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)',
                        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
            Vue d'ensemble
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 32 }}>
            <StatCard navigate={navigate} icon={ReceiptText} color="#EA580C" label="Notes de frais en attente"
              value={loading ? '–' : nfPending} sub={`${notesFrais.length} au total`}
              to="/notes-frais" badge={nfPending > 0 ? `${nfPending} en attente` : null} />
            <StatCard navigate={navigate} icon={CalendarX} color="#7C3AED" label="Absences en attente"
              value={loading ? '–' : abPending} sub={`${absences.length} au total`}
              to="/absences" badge={abPending > 0 ? `${abPending} en attente` : null} />
            <StatCard navigate={navigate} icon={FolderOpen} color="#0891B2" label="Documents déposés"
              value={loading ? '–' : docs.length} sub="Contrat, pièce d'identité…"
              to="/mes-documents" badge={docsRejetes > 0 ? `${docsRejetes} rejeté(s)` : null} />
            <StatCard navigate={navigate} icon={Calendar} color="#059669" label="Semaine en cours"
              value={loading ? '–' : `S${weekNum}`} sub={`${MONTHS_FR[month - 1]} ${year}`}
              to={null} badge={null} />
          </div>
        </div>

        {/* Actions rapides */}
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)',
                        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
            Actions rapides
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <ActionBtn navigate={navigate} icon={Timer} color="#3B82F6"
              label="Feuille de temps" desc={`Mois de ${MONTHS_FR[month - 1]}`}
              to={`/timesheets/${year}/${month}`} />
            <ActionBtn navigate={navigate} icon={ReceiptText} color="#EA580C"
              label="Note de frais" desc="Déposer un remboursement"
              to="/notes-frais/nouveau" />
            <ActionBtn navigate={navigate} icon={CalendarX} color="#7C3AED"
              label="Demande d'absence" desc="Congés, RTT, arrêt maladie…"
              to="/absences/new" />
            <ActionBtn navigate={navigate} icon={FolderOpen} color="#0891B2"
              label="Mes Documents" desc="Contrat, pièce d'identité…"
              to="/mes-documents" />
          </div>
        </div>
      </div>
    </div>
  );
}
